import { Reel } from "@lib/titan2-slingo/dist/game/view/abstract/display/Reel";
import { ReelSpinSymbol } from "@lib/titan2-slingo/dist/game/view/abstract/display/ReelSpinSymbol";
import { SlingoStyle } from "@lib/titan2-slingo/dist/game/view/abstract/style/SlingoStyle";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { GameEvent } from "../../../controller/event/GameEvent";
import { GameStyle } from "../style/GameStyle";
import { GameReelSymbol } from "./GameReelSymbol";
/**
 * GameReel.ts
 */
export class GameReel extends Reel {
    create() {
        super.create();
        this.setTrackingId(GameReel.getTrackingId(this.index));
    }
    createMask() {
        super.createMask();
        this.reelMask.position.set(-this.reelMask.width / 2, -this.reelMask.height / 2);
    }
    createReelSymbols(reelSet) {
        return reelSet.map(id => {
            // Don't remake a symbol if it's already part of reelSetSymbolsStandard0
            const existingSymbol = this.getReelSymbols(id);
            if (existingSymbol) {
                return existingSymbol;
            }
            let item;
            if (this.landscapeProperties.symbolIDToStaticMap.has(id) || this.portraitProperties?.symbolIDToStaticMap.has(id)) {
                item = TitanSprite.createSprite(this.reelSetSymbolHolder, "item", this.landscapeProperties.symbolIDToStaticMap.get(id), this.portraitProperties?.symbolIDToStaticMap.get(id));
            }
            else {
                item = new TitanText(this.landscapeProperties.symbolProperties, this.portraitProperties?.symbolProperties);
                this.reelSetSymbolHolder.addChild(item);
                item.setText(id);
            }
            return new ReelSpinSymbol(id, item);
        });
    }
    createSymbolAnimations() {
        this.symbolAnimation = new GameReelSymbol(this.landscapeProperties.symbolAnimationSpineProperties, this.portraitProperties?.symbolAnimationSpineProperties, this.uiProperties);
    }
    removeSymbolAnimation() {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.symbolAnimation.showEmpty(this.index);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(GameEvent.NEXT_GAME_TRANSITION, this.handleNextGameTransition);
        this.registerGame(GameEvent.SCATTER_WIN_COLLECT, this.collectScatter);
        this.registerGame(GameEvent.SCATTER_WIN_SUSPENSE_CANCELLED, this.cancelScatterSuspense);
    }
    async handleNextGameTransition() {
        const column = this.index;
        const row = this.model.getTicket().getNumberOfRows();
        const delay = (column + row) * this.uiProperties.transitionStagger;
        await new Promise(resolve => this.callAfter(delay, resolve));
        await this.symbolAnimation.fadeOut();
        this.symbolAnimation.reset();
    }
    collectNumber() {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.symbolAnimation.collectNumber();
    }
    collectFreeSpin() {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.symbolAnimation.collectFreeSpin();
    }
    collectJoker() {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.symbolAnimation.collectJoker();
    }
    collectSuperJoker() {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.symbolAnimation.collectSuperJoker();
    }
    activateJoker() {
        if (!this.columnFullyDabbed()) {
            this.symbolAnimation.activateJoker();
        }
    }
    activateSuperJoker() {
        this.symbolAnimation.activateSuperJoker();
    }
    suspenseScatter() {
        if (this.currentId === GameStyle.SCATTER) {
            this.symbolAnimation.suspenseScatter();
        }
    }
    collectScatter() {
        if (this.currentId === GameStyle.SCATTER) {
            this.symbolAnimation.collectScatter();
        }
    }
    async awaitScatterSuspenseDelay() {
        return this.symbolAnimation.awaitScatterSuspenseDelay();
    }
    async stopScatterSuspense() {
        if (this.currentId === GameStyle.SCATTER) {
            await this.symbolAnimation.stopScatterSuspense();
        }
    }
    stopScatterWithHighlight() {
        if (this.currentId === GameStyle.SCATTER) {
            this.symbolAnimation.stopScatterWithHighlight();
        }
    }
    cancelScatterSuspense() {
        if (this.currentId === GameStyle.SCATTER) {
            this.symbolAnimation.cancelScatterSuspense();
        }
    }
    createSuspenseAnimation() {
        super.createSuspenseAnimation();
        if (this.suspenseAnimation) {
            this.suspenseAnimation.setSkin(`column/${this.index}`);
        }
    }
    addSymbol(id) {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.currentId = id;
        switch (this.currentId) {
            case GameStyle.JOKER:
                if (this.columnFullyDabbed()) {
                    this.symbolAnimation.stopJoker();
                }
                else {
                    this.symbolAnimation.strikeJoker();
                }
                break;
            case GameStyle.SUPER_JOKER:
                this.symbolAnimation.strikeSuperJoker();
                break;
            case GameStyle.FREE_SPIN:
                this.symbolAnimation.strikeFreeSpin();
                break;
            case GameStyle.SCATTER:
                this.symbolAnimation.strikeScatter();
                break;
            default:
                const number = parseInt(id, 10);
                if (isNaN(number)) {
                    this.symbolAnimation.strikeScatter();
                }
                else if (this.idMatchesTicket(id)) {
                    this.symbolAnimation.strikeNumber(number);
                }
                else {
                    this.symbolAnimation.stopNumber(number);
                }
                break;
        }
        //add a little bump when it stops
        gsap.from(this.symbolHolder, { y: "+=12", duration: 0.3, ease: "back.out(3)" });
    }
    addDisabled() {
        // This method is called both on recovery and when initiating a new spin.
        // When called during the initiation of a spin, a dabbed symbol will still be animating.
        // We want dabbed symbols to animate out smoothly, rather than immediately popping to a disabled state.
        if (this.symbolAnimation && !this.model.getDabData().getSelectedCell()) {
            switch (this.currentId) {
                case GameStyle.JOKER:
                    this.symbolAnimation.stopJoker();
                    break;
                case GameStyle.SUPER_JOKER:
                    this.symbolAnimation.stopSuperJoker();
                    break;
                case GameStyle.FREE_SPIN:
                    this.symbolAnimation.stopFreeSpin();
                    break;
                case GameStyle.SCATTER:
                    this.symbolAnimation.stopScatter();
                    break;
                default:
                    this.symbolAnimation.stopNumber();
                    break;
            }
        }
        this.addChild(this.disabled);
    }
    static getTrackingId(column) {
        return `${SlingoStyle.REEL}_${column}`;
    }
}
//# sourceMappingURL=GameReel.js.map